import { AuthContext } from "@/auth/authcontext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSignup } from "@/hooks/api/use-auth";
import { FormEvent, useContext, useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

interface ApiError {
  errors?: Record<string, string[]>;
  detail?: string;
  non_field_errors?: string[];
  message?: string;
}

export default function SignupPage() {
  const { setUserDetails } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
  });

  const signup = useSignup();

  function validateForm(): boolean {
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return false;
    }
    return true;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validateForm()) return;

    const apiData = {
      ...formData,
      password1: formData.password,
    };

    signup.mutate(apiData, {
      onSuccess: (data) => {
        localStorage.setItem("refreshToken", data.refresh);
        localStorage.setItem("accessToken", data.access);
        setUserDetails({
          access: data.access,
          refresh: data.refresh,
          user: data.user,
        });
        toast.success("Account created successfully!");
        navigate("/");
      },
      onError: (error: ApiError) => {
        if (error.errors) {
          Object.entries(error.errors).forEach(([field, messages]) => {
            (messages as string[])?.forEach((message) => {
              toast.error(`${field}: ${message}`);
            });
          });
        } else if (error.detail) {
          toast.error(error.detail);
        } else if (error.non_field_errors) {
          error.non_field_errors.forEach((message) => {
            toast.error(message);
          });
        } else {
          toast.error(
            error.message || "Failed to create account. Please try again."
          );
        }
      },
    });
  }

  return (
    <div className="container flex h-screen w-screen flex-col items-center justify-center">
      <Card className="mx-auto max-w-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">
            Create an Account
          </CardTitle>
          <CardDescription>
            Enter your details to create a new account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  type="text"
                  id="first_name"
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      first_name: e.target.value,
                    }))
                  }
                  value={formData.first_name}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  type="text"
                  id="last_name"
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      last_name: e.target.value,
                    }))
                  }
                  value={formData.last_name}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  placeholder="m@example.com"
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  value={formData.email}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  type="password"
                  id="password"
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  value={formData.password}
                />
              </div>
              <Button
                className="w-full"
                type="submit"
                disabled={signup.isPending}
              >
                {signup.isPending ? "Creating Account..." : "Create Account"}
              </Button>
            </div>
          </form>
          <div className="mt-4 text-center text-sm">
            Already have an account?{" "}
            <Link to="/login" className="underline">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
